import { Timestamp } from '@firebase/firestore';

interface FormatOptions {
  suffix?: string;
  longFallbackAfterWeeks?: number;
}

/**
 * Format a Firestore Timestamp as a relative time string.
 * Returns "just now", "5m ago", "2h ago", "3d ago", etc.
 */
export function formatRelativeTime(
  timestamp: Timestamp | undefined | null,
  options: FormatOptions = {}
): string | null {
  return formatRelativeTimeFromMillis(timestamp ? timestamp.toMillis() : null, options);
}

/**
 * Same formatting for callers that hold epoch millis instead of a Timestamp
 * (keeps screens free of @firebase imports per the layering rule).
 */
export function formatRelativeTimeFromMillis(
  then: number | null | undefined,
  options: FormatOptions = {}
): string | null {
  if (then == null) return null;

  const { suffix } = options;

  const now = Date.now();
  const diffMs = now - then;

  // "just now" is already a complete phrase — appending the caller's suffix
  // would render as "just now ago".
  if (diffMs < 0) return 'just now';

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return withSuffix(`${minutes}m`, suffix);

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return withSuffix(`${hours}h`, suffix);

  const days = Math.floor(hours / 24);
  if (days < 30) return withSuffix(`${days}d`, suffix);

  const months = Math.floor(days / 30);
  return withSuffix(`${months}mo`, suffix);
}

function withSuffix(value: string, suffix?: string): string {
  return suffix ? `${value} ${suffix}` : value;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Absolute short date for the news kicker: "Aug 20", or "Aug 20, 2025" once
 * the year differs from today's. News is dated content — "3d ago" reads
 * fine on an observation but wrong on an announcement.
 */
export function formatShortDate(timestamp: Timestamp | undefined | null): string | null {
  if (!timestamp) return null;
  const d = new Date(timestamp.toMillis());
  const base = `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === new Date().getFullYear() ? base : `${base}, ${d.getFullYear()}`;
}
